// PageHeader: the short top of a page (drawing v2.6 `header()` on the list,
// dashboard and form screens) and, as `variant="record"`, the fixed hero of a
// record page (screen 7, treatment A: `.hero`). Standard rule 1: an eyebrow
// saying whose data this is, the title, one sentence, no paragraphs; a record
// page uses breadcrumbs instead of an eyebrow, with a 44px back link under 1024.
//
// Fixed chrome (customization contract rule 4): the header, the scope, the
// Cleanup entry point and the next-step button are drawn here, never as a
// zone, so no saved layout can hide them.

import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cx } from "./cx";

export interface Crumb {
  label: string;
  /** A route for every crumb but the last, which is the page itself. */
  to?: string;
}

function Crumbs({ crumbs }: { crumbs: Crumb[] }) {
  const back = [...crumbs].reverse().find((c) => c.to);
  return (
    <>
      <nav className="pg-crumbs" aria-label="Breadcrumb">
        {crumbs.map((c, i) => (
          <span key={`${c.label}-${i}`} style={{ display: "contents" }}>
            {i > 0 && <ChevronRight className="pg-ico pg-ico--s" aria-hidden />}
            {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
          </span>
        ))}
      </nav>
      {back && (
        <Link className="pg-back" to={back.to!}>
          <ChevronLeft className="pg-ico pg-ico--s" aria-hidden />
          {back.label}
        </Link>
      )}
    </>
  );
}

export interface PageHeaderProps {
  /** Whose data this is: "Insurance · My book". Not drawn on a record page. */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** A count beside the title ("Households 1,284"), in the page sentence's type. */
  count?: ReactNode;
  /** One sentence. Hidden on a phone when a count is shown. */
  sentence?: ReactNode;
  /** Breadcrumbs; a record page uses these instead of an eyebrow. */
  crumbs?: Crumb[];
  /** The buttons: one primary at most (the standard's rule). */
  actions?: ReactNode;
  /** `record` draws the hero card (screen 7); `page` the plain header. */
  variant?: "page" | "record";
  /** Record hero: the people and city line, with at most one badge. */
  who?: ReactNode;
  /** Record hero: the key facts row ("Insurance advisor Joy Smith"). */
  facts?: ReactNode;
  /** Record hero: the people tile under the facts (CLAUDE.md, every record names a person). */
  people?: ReactNode;
  /** Record hero: anything else the hero carries (a status line of pills, a split notice). */
  extra?: ReactNode;
  id?: string;
}

export function PageHeader({
  eyebrow,
  title,
  count,
  sentence,
  crumbs,
  actions,
  variant = "page",
  who,
  facts,
  people,
  extra,
  id,
}: PageHeaderProps) {
  if (variant === "record") {
    return (
      <header className="pg-card pg-hero" id={id}>
        <div className="pg-hero__top">
          <div>
            {crumbs && <Crumbs crumbs={crumbs} />}
            <h1 className="pg-title">{title}</h1>
            {who && <div className="pg-hero__who">{who}</div>}
          </div>
          {actions && <div className="pg-hero__acts">{actions}</div>}
        </div>
        {facts && <div className="pg-facts">{facts}</div>}
        {extra}
        {people}
      </header>
    );
  }
  return (
    <header className="pg-head" id={id}>
      <div>
        {crumbs && <Crumbs crumbs={crumbs} />}
        {eyebrow && <div className="pg-eyebrow">{eyebrow}</div>}
        <h1 className="pg-title">
          {title}
          {count !== undefined && <span className="pg-count">{count}</span>}
        </h1>
        {sentence && <p className={cx("pg-head__sentence", count !== undefined && "pg-hide-p")}>{sentence}</p>}
      </div>
      {actions && <div className="pg-acts">{actions}</div>}
    </header>
  );
}

/** A fact in the hero's facts row: "Client since <b>2019</b>". */
export function Fact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <span>
      {label} <b>{children}</b>
    </span>
  );
}
